"use client"

import { useState, useCallback, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Clock, AlertTriangle, CheckCircle, Activity, Globe, Zap, XCircle } from "lucide-react"

// Proper interfaces untuk type safety
interface SystemStats {
  avgResponseTime?: number
  requestsPerSecond?: number
  cpuUsage?: number
  memoryUsage?: number
  networkUsage?: number
  systemHealth?: number
  uptime?: number
  errorRate?: number
}

interface Alert {
  id: number
  type: "warning" | "info" | "error"
  message: string
  time: string
  severity: "low" | "medium" | "high"
}

interface CountryData {
  country: string
  code: string
  requests: number
  percentage: number
}

interface DashboardRightSidebarProps {
  currentTime?: Date
  systemStats?: SystemStats
  className?: string
}

export function DashboardRightSidebar({ currentTime, systemStats = {}, className = "" }: DashboardRightSidebarProps) {
  const [localTime, setLocalTime] = useState<Date>(currentTime || new Date())
  const [error, setError] = useState<string | null>(null)

  // Update local time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setLocalTime(currentTime || new Date())
    }, 1000)

    return () => clearInterval(interval)
  }, [currentTime])

  // Safe formatting functions
  const formatNumber = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0"
    return num.toLocaleString()
  }, [])

  const formatPercentage = useCallback((num: number | undefined | null): number => {
    if (typeof num !== "number" || isNaN(num)) return 0
    return Math.max(0, Math.min(100, num))
  }, [])

  const formatTime = useCallback((date: Date | undefined | null): string => {
    try {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return new Date().toLocaleTimeString()
      }
      return date.toLocaleTimeString()
    } catch (error) {
      console.error("Error formatting time:", error)
      return "00:00:00"
    }
  }, [])

  const formatDate = useCallback((date: Date | undefined | null): string => {
    try {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return new Date().toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      }
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    } catch (error) {
      console.error("Error formatting date:", error)
      return "Invalid Date"
    }
  }, [])

  // Safe data dengan proper validation
  const safeSystemStats = {
    avgResponseTime: systemStats?.avgResponseTime || 0,
    requestsPerSecond: systemStats?.requestsPerSecond || 0,
    cpuUsage: formatPercentage(systemStats?.cpuUsage || 23),
    memoryUsage: formatPercentage(systemStats?.memoryUsage || 67),
    networkUsage: formatPercentage(systemStats?.networkUsage || 45),
    systemHealth: formatPercentage(systemStats?.systemHealth || 95),
    uptime: systemStats?.uptime || 99.98,
    errorRate: systemStats?.errorRate || 0.02,
  }

  // Safe static data dengan proper typing
  const alerts: Alert[] = [
    {
      id: 1,
      type: "warning",
      message: "High bot activity from AS16509",
      time: "2 min ago",
      severity: "medium",
    },
    {
      id: 2,
      type: "info",
      message: "Campaign A conversion rate up 15%",
      time: "5 min ago",
      severity: "low",
    },
    {
      id: 3,
      type: "error",
      message: "Rate limit exceeded for 45.XXX.XXX.89",
      time: "8 min ago",
      severity: "high",
    },
  ]

  const topCountries: CountryData[] = [
    { country: "Indonesia", code: "ID", requests: 4521, percentage: 35 },
    { country: "United States", code: "US", requests: 2847, percentage: 22 },
    { country: "Singapore", code: "SG", requests: 1923, percentage: 15 },
    { country: "Malaysia", code: "MY", requests: 1456, percentage: 11 },
    { country: "Thailand", code: "TH", requests: 892, percentage: 7 },
  ]

  // Safe alert rendering function
  const renderAlert = useCallback((alert: Alert) => {
    try {
      const alertStyles = {
        high: "bg-red-900/30 border-red-700/50 text-red-300",
        medium: "bg-yellow-900/30 border-yellow-700/50 text-yellow-300",
        low: "bg-blue-900/30 border-blue-700/50 text-blue-300",
      }

      return (
        <div key={alert.id} className={`p-3 rounded-lg border ${alertStyles[alert.severity]}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm">{alert.message}</p>
              <p className="text-xs text-slate-400 mt-1">{alert.time}</p>
            </div>
            <Badge variant={alert.severity === "high" ? "destructive" : "secondary"} className="text-xs ml-2">
              {alert.severity}
            </Badge>
          </div>
        </div>
      )
    } catch (error) {
      console.error("Error rendering alert:", error)
      return null
    }
  }, [])

  // Safe country rendering function
  const renderCountry = useCallback(
    (country: CountryData, index: number) => {
      try {
        return (
          <div key={`${country.code}-${index}`} className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-slate-600 rounded flex items-center justify-center text-xs font-medium">
                {country.code}
              </div>
              <div>
                <p className="text-sm text-white">{country.country}</p>
                <p className="text-xs text-slate-400">{formatNumber(country.requests)} requests</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-purple-400 font-medium">{country.percentage}%</p>
            </div>
          </div>
        )
      } catch (error) {
        console.error("Error rendering country:", error)
        return null
      }
    },
    [formatNumber],
  )

  // System health status
  const getHealthStatus = useCallback(() => {
    const health = safeSystemStats.systemHealth
    if (health >= 90) return { status: "Healthy", color: "text-green-300", icon: CheckCircle }
    if (health >= 70) return { status: "Warning", color: "text-yellow-300", icon: AlertTriangle }
    return { status: "Critical", color: "text-red-300", icon: XCircle }
  }, [safeSystemStats.systemHealth])

  const healthStatus = getHealthStatus()
  const HealthIcon = healthStatus.icon

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <XCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* System Time */}
      <Card className="bg-slate-800/50 backdrop-blur-xl border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center">
            <Clock className="w-5 h-5 mr-2 text-purple-400" />
            System Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-2xl font-mono text-white">{formatTime(localTime)}</div>
            <div className="text-sm text-slate-400 mt-1">{formatDate(localTime)}</div>
          </div>
        </CardContent>
      </Card>

      {/* System Health */}
      <Card className="bg-slate-800/50 backdrop-blur-xl border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center">
            <Activity className="w-5 h-5 mr-2 text-green-400" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">CPU Usage</span>
              <span className="text-green-400 font-mono">{safeSystemStats.cpuUsage}%</span>
            </div>
            <Progress value={safeSystemStats.cpuUsage} className="h-2" />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Memory</span>
              <span className="text-yellow-400 font-mono">{safeSystemStats.memoryUsage}%</span>
            </div>
            <Progress value={safeSystemStats.memoryUsage} className="h-2" />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Network</span>
              <span className="text-blue-400 font-mono">{safeSystemStats.networkUsage}%</span>
            </div>
            <Progress value={safeSystemStats.networkUsage} className="h-2" />
          </div>

          <div className="pt-2 border-t border-slate-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Overall Health</span>
              <Badge className={`bg-green-900/30 border-green-700 ${healthStatus.color}`}>
                <HealthIcon className="w-3 h-3 mr-1" />
                {healthStatus.status}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Alerts */}
      <Card className="bg-slate-800/50 backdrop-blur-xl border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-yellow-400" />
            Live Alerts
            <Badge className="ml-2 bg-red-500/20 text-red-400 border-red-500/50 text-xs">{alerts.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {alerts.length > 0 ? (
              alerts.map(renderAlert).filter(Boolean)
            ) : (
              <div className="text-center py-4 text-slate-500">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No active alerts</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Countries */}
      <Card className="bg-slate-800/50 backdrop-blur-xl border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center">
            <Globe className="w-5 h-5 mr-2 text-blue-400" />
            Top Countries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topCountries.length > 0 ? (
              topCountries.map(renderCountry).filter(Boolean)
            ) : (
              <div className="text-center py-4 text-slate-500">
                <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No country data available</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <Card className="bg-slate-800/50 backdrop-blur-xl border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center">
            <Zap className="w-5 h-5 mr-2 text-yellow-400" />
            Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-300">Uptime</span>
            <span className="text-green-400 font-medium font-mono">{safeSystemStats.uptime}%</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-300">Avg Response</span>
            <span className="text-blue-400 font-medium font-mono">
              {formatNumber(safeSystemStats.avgResponseTime)}ms
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-300">Throughput</span>
            <span className="text-purple-400 font-medium font-mono">
              {formatNumber(safeSystemStats.requestsPerSecond)}/s
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-300">Error Rate</span>
            <span className="text-red-400 font-medium font-mono">{safeSystemStats.errorRate}%</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
